import {put, call, all} from 'redux-saga/effects';
import Referral from "../../entities/referral";
import * as Actions from "../actions/referralActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ReferralAction = {
    referralId: string;
    referral: Referral;
    referrals: Referral[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneReferral(action: ReferralAction) {
	const session = tabSession.captureGeneration();
	try {
		const referral: Referral = yield call(api.findOne, 'referral', action.referralId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_FETCH_ONE_SUCCESS, referralId: action.referralId, referral, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_FETCH_ONE_FAILURE, referralId: action.referralId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ReferralAction} action
 */
export function* saveReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REFERRAL_SAVE saga', action.referral?.id);

		if (action.referral === undefined || action.referral === null) {
			console.error('saveReferral received a null');
			throw new Error('saveReferral received a null');
		}

		if (!(action.referral instanceof Referral)) {
			action.referral = new Referral(action.referral);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.referral.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.referral);
			const addResult = yield call(idb.add, 'referral', action.referral);
			if (addResult.isErr()) {
				console.error(`⮂ saveReferral: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertReferralSave(action.referral, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.REFERRAL_SAVE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.REFERRAL_SAVE, referral: action.referral, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertReferralSave(action.referral, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.REFERRAL_SAVE_FAILURE, referral: action.referral, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ReferralAction} action
 * @param {Referral[]} action.referrals
 */
export function* saveManyReferrals(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ REFERRAL_SAVE_MANY saga', action.referrals.map(referral => referral.id).join(','));

        if (!Array.isArray(action.referrals)) {
            console.error('saveManyReferrals expected an array, received', action.referrals);
            throw new Error('saveManyReferrals expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveReferral), under these queue keys
		const dispatchedAddedOns: number[] = action.referrals.map(referral => referral?.addedOn);
		const revertAll = () => action.referrals.map((referral, i) =>
			put(Actions.revertReferralSave(referral, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Referral[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.referrals.length; i++) {
				const referral = action.referrals[i];
				referral.persisted = false;
				ensureOperationId(referral);
				toSave.push(referral);
			}

			const addResult = yield call(idb.add, 'referral', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyReferrals: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.REFERRAL_SAVE_MANY_FAILURE, referrals: action.referrals, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.REFERRAL_SAVE_MANY, referrals: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.REFERRAL_SAVE_MANY_FAILURE, referrals: action.referrals, status: 500, err});

			console.error(`Error saving ${action.referrals.length} referrals to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ReferralAction} action
 */
export function* removeReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.referral === undefined || action.referral === null || !(action.referral instanceof Referral)) {
			console.error('removeReferral expected type Referral, received', action.referral);
			reportQueued(false);
			throw new Error('removeReferral expected type Referral');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.referral.status = 'D';
		action.referral.addedOn = newHRTimestamp();
		action.referral.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.referral);
		const addResult = yield call(idb.add, 'referral', action.referral);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeReferral: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.REFERRAL_REMOVE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_REMOVE, referral: action.referral});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_REMOVE_FAILURE, referral: action.referral, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Referral.
	*
	* @param {ReferralAction} action
	*/
export function* restoreReferral(action: ReferralAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreReferral expected getCurrentUserId() to return a value');
        return;
    }

	if (action.referral === undefined || action.referral === null) {
        console.error('restoreReferral expected a Referral, received null');
        throw new Error('restoreReferral expected a Referral, received null');
    }
    else if (!(action.referral instanceof Referral)) action.referral = new Referral(action.referral); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.referral.status = 'U';
		action.referral.addedOn = newHRTimestamp();
		action.referral.addedBy = userId;
		action.referral.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.referral);
		const addResult = yield call(idb.add, 'referral', action.referral);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreReferral: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.REFERRAL_RESTORE_FAILURE, referral: action.referral, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.REFERRAL_RESTORE, referral: action.referral});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.REFERRAL_RESTORE_FAILURE, referral: action.referral, status: 500, err});

		console.error('Error restoring from database', err);
	}
}